import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import Nav from "@/components/landing/Nav";
import Footer from "@/components/landing/Footer";
import Analytics from "./components/shared/Analytics";
import LandingPage from "@/pages/LandingPage";
import WomensShop from "@/pages/WomensShop";
import MensGarage from "@/pages/MensGarage";
import MedicationPage from "@/pages/MedicationPage";
import BestMedicationPage from "@/pages/BestMedicationPage";
import FAQPage from "@/pages/FAQPage";
import ProvidersPage from "@/pages/ProvidersPage";
import ResultsPage from "@/pages/ResultsPage";
import CategoryHub from "@/pages/CategoryHub";
import CityServicePage from "@/pages/CityServicePage";
import ArticlePage, { ResourceCenter } from "@/pages/ArticlePage";
import CityOverviewPage from "@/pages/CityOverviewPage";
import CommandCenter from "@/pages/CommandCenter";
import { AboutPage, ContactPage } from "@/pages/StaticPages";
import { PrivacyPage, TermsPage, SmsConsentPage, HipaaPage } from "@/pages/LegalPages";
import {
  RockwallCountyHubPage, RockwallTirzepatidePage, RockwallSemaglutidePage,
  RockwallCityPage, RoyseCityPage, FateCityPage, HeathCityPage, MclendonChisholmCityPage,
  RockwallCountyHubPageEs, RockwallCityPageEs, RoyseCityPageEs, FateCityPageEs, HeathCityPageEs,
} from "@/pages/RockwallSEOPages";
import LeadProvider from "@/components/shared/LeadProvider";

// Strip stale description metas added by 3rd-party scripts so Helmet stays authoritative.
function useDescriptionGuard() {
  useEffect(() => {
    const strip = () => {
      const tags = document.querySelectorAll("meta[name='description']");
      const stale = Array.from(tags).filter((t) =>
        (t.getAttribute("content") || "").toLowerCase().includes("emergent.sh")
      );
      stale.forEach((t) => t.parentNode && t.parentNode.removeChild(t));
    };
    strip();
    const obs = new MutationObserver(strip);
    obs.observe(document.head, { childList: true, subtree: true });
    return () => obs.disconnect();
  }, []);
}

function NotFoundPage() {
  return (
    <main className="min-h-screen bg-[#06070A] text-white flex flex-col" data-testid="not-found-page">
      <Nav />
      <div className="flex-1 flex items-center justify-center px-5 py-24 text-center">
        <div>
          <p className="text-[11px] tracking-[0.24em] font-extrabold uppercase" style={{ color: "#E11D2A" }}>404</p>
          <h1 className="mt-3 font-heavy text-5xl sm:text-6xl">PAGE NOT FOUND</h1>
          <p className="mt-4 text-white/60 max-w-[460px] mx-auto">
            The page you're looking for doesn't exist or has moved. Let's get you back on track.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/" data-testid="notfound-home"
              className="px-6 py-3 text-[12px] font-extrabold tracking-[0.14em] uppercase text-white rounded-md"
              style={{ background: "#E11D2A" }}>Back Home</Link>
            <Link to="/category/weight-loss" data-testid="notfound-treatments"
              className="px-6 py-3 text-[12px] font-extrabold tracking-[0.14em] uppercase rounded-md border border-white/25 text-white">
              Explore Treatments</Link>
          </div>
        </div>
      </div>
      <Footer />
    </main>
  );
}

function App() {
  useDescriptionGuard();
  return (
    <div className="App">
      <BrowserRouter>
        <Analytics />
        <LeadProvider>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/womens-shop" element={<WomensShop />} />
            <Route path="/mens-garage" element={<MensGarage />} />
            <Route path="/results" element={<ResultsPage />} />
            <Route path="/medications/:slug" element={<MedicationPage />} />
            <Route path="/best/:slug" element={<BestMedicationPage />} />
            <Route path="/best/:city/:slug" element={<BestMedicationPage />} />
            <Route path="/category/:slug" element={<CategoryHub />} />
            <Route path="/admin" element={<CommandCenter />} />
            <Route path="/locations/:city/:service" element={<CityServicePage />} />
            <Route path="/locations/:city" element={<CityOverviewPage />} />
            <Route path="/learn" element={<ResourceCenter />} />
            <Route path="/learn/:slug" element={<ArticlePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/providers" element={<ProvidersPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/sms-consent" element={<SmsConsentPage />} />
            <Route path="/hipaa" element={<HipaaPage />} />
            {/* ─── Rockwall County SEO authority cluster ─── */}
            <Route path="/rockwall-county-weight-loss" element={<RockwallCountyHubPage />} />
            <Route path="/rockwall-county-tirzepatide" element={<RockwallTirzepatidePage />} />
            <Route path="/rockwall-county-semaglutide" element={<RockwallSemaglutidePage />} />
            <Route path="/rockwall-weight-loss" element={<RockwallCityPage />} />
            <Route path="/royse-city-weight-loss" element={<RoyseCityPage />} />
            <Route path="/fate-weight-loss" element={<FateCityPage />} />
            <Route path="/heath-weight-loss" element={<HeathCityPage />} />
            <Route path="/mclendon-chisholm-weight-loss" element={<MclendonChisholmCityPage />} />
            {/* ─── Spanish counterparts (hreflang-linked) ─── */}
            <Route path="/es/rockwall-county-weight-loss" element={<RockwallCountyHubPageEs />} />
            <Route path="/es/rockwall-weight-loss" element={<RockwallCityPageEs />} />
            <Route path="/es/royse-city-weight-loss" element={<RoyseCityPageEs />} />
            <Route path="/es/fate-weight-loss" element={<FateCityPageEs />} />
            <Route path="/es/heath-weight-loss" element={<HeathCityPageEs />} />
            {/* Catch-all — never render a blank document for unknown/stale URLs. */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </LeadProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
